import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Json } from "@obsn/shared/db";
import type { Unterpunkt } from "../../kern/modul";
import { useAngemeldet } from "../../kern/sitzung";
import { db } from "../../kern/supabase";
import { useRessortFarben } from "./daten";
import type { VorhabenArt } from "../../kern/kapsel";
import { alsArt, alsStand, kapselUnterpunkte, meineVorhaben, type AntragStand, type Summen, type VorhabenKarte, type VorhabenRolle } from "./vorhabenlogik";

/* Daten der Vorhaben (Kapseln, BAUPLAN-PAKETE.md, Abschnitt 9). Lesen über die View aufgaben_vorhaben_uebersicht (security_invoker:
   Mitglieder sehen nur ihre Vorhaben, Zahlen nur aus Aufgaben, die sie sehen), Schreiben nur über RPCs aus Paket C1. */

type Zeile = Record<string, unknown>;

const rolle = (x: unknown): VorhabenRolle | null => (x === "leitend" || x === "mitwirkend" ? x : null);
const ids = (x: unknown): string[] => (Array.isArray(x) ? x.filter((y): y is string => typeof y === "string") : []);

export function alsVorhaben(z: Zeile): VorhabenKarte {
  return {
    id: String(z.id),
    name: String(z.name ?? ""),
    beschreibung: String(z.beschreibung ?? ""),
    beginn: (z.beginn as string | null) ?? null,
    ende: (z.ende as string | null) ?? null,
    aktiv: z.aktiv !== false,
    ressort_id: (z.ressort_id as string | null) ?? null,
    aufgaben_gesamt: Number(z.aufgaben_gesamt ?? 0),
    aufgaben_offen: Number(z.aufgaben_offen ?? 0),
    aufgaben_erledigt: Number(z.aufgaben_erledigt ?? 0),
    naechste_frist: (z.naechste_frist as string | null) ?? null,
    leitung: ids(z.leitung),
    personen: ids(z.personen),
    meine_rolle: rolle(z.meine_rolle),
    drive_url: (z.drive_url as string | null) ?? null,
    archiviert_am: (z.archiviert_am as string | null) ?? null,
    programm_beginn: (z.programm_beginn as string | null) ?? null,
    programm_ende: (z.programm_ende as string | null) ?? null,
    ort: String(z.ort ?? ""),
    // Arten (BAUPLAN-VORHABEN-ARTEN.md, Abschnitt 4): Fehlt die Spalte noch, ist jedes Vorhaben eine Veranstaltung
    art: alsArt(z.art),
    art_bekannt: "art" in z,
    gehoert_zu: (z.gehoert_zu as string | null | undefined) ?? null,
    antrag_stand: alsStand(z.antrag_stand),
    foerdergeber: String(z.foerdergeber ?? ""),
    antragsfrist: (z.antragsfrist as string | null | undefined) ?? null,
    nachweisfrist: (z.nachweisfrist as string | null | undefined) ?? null,
  };
}

/** Euro aus der Datenbank (numeric, als Zahl oder Text) in Cent */
const cent = (x: unknown): number | null => (x === null || x === undefined || x === "" ? null : Math.round(Number(x) * 100));

/** Summen der Anträge, die die Person sehen darf (RLS: Vorstand mit finanzen.verwalten und Leitung). Wer sie nicht sehen darf, bekommt
 *  keine Zeile. Fehlt die Tabelle noch (Minuten nach dem Deploy) oder geht das Lesen schief: still keine Summen. */
export function useVorhabenSummen() {
  return useQuery({
    queryKey: ["vorhaben", "summen"],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Map<string, Summen>> => {
      const { data, error } = await db().from("aufgaben_vorhaben_summen").select("vorhaben_id, beantragt, bewilligt");
      if (error) return new Map();
      return new Map((data as Zeile[]).map((z) => [String(z.vorhaben_id), { beantragt: cent(z.beantragt), bewilligt: cent(z.bewilligt) }]));
    },
  });
}

/** Fehlt in der Datenbank noch, was die neue Oberfläche braucht (Funktion, Tabelle oder Spalte)? Dann läuft die Migration gleich. */
export function aktualisierungFehlt(e: unknown): boolean {
  const f = (typeof e === "object" && e !== null ? e : {}) as { code?: unknown; message?: unknown };
  const code = typeof f.code === "string" ? f.code : "";
  return ["PGRST202", "PGRST205", "42883", "42P01", "42703"].includes(code) || (code === "22023" && f.message === "Unbekanntes Feld.");
}
export const AKTUALISIERUNG = "Die App wird gerade aktualisiert. Versuch es in ein paar Minuten noch einmal.";

/** Alle sichtbaren Vorhaben, auch archivierte. Dieselbe Abfrage speist Navigation, Woche und die Seiten, deshalb aus dem Zwischenspeicher. */
export function useVorhabenUebersicht() {
  return useQuery({
    queryKey: ["vorhaben", "uebersicht"],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<VorhabenKarte[]> => {
      const { data, error } = await db().from("aufgaben_vorhaben_uebersicht").select("*").order("name");
      if (error) throw error;
      return (data as Zeile[]).map(alsVorhaben);
    },
  });
}

/** Eigene Rolle je Vorhaben, für Rechte in Listen und Dialog (Leitung bearbeitet in ihrem Vorhaben, wählt Personen daraus). */
export function useKapsel() {
  const q = useVorhabenUebersicht();
  const nachId = useMemo(() => new Map((q.data ?? []).map((v) => [v.id, v])), [q.data]);
  return {
    rolle: (id: string | null): VorhabenRolle | null => (id ? (nachId.get(id)?.meine_rolle ?? null) : null),
    leitet: (id: string | null): boolean => !!id && nachId.get(id)?.meine_rolle === "leitend",
    personenVon: (id: string | null): readonly string[] => (id ? (nachId.get(id)?.personen ?? []) : []),
    name: (id: string | null): string | null => (id ? (nachId.get(id)?.name ?? null) : null),
  };
}

/** Navigation (Abschnitt 9.1): Der Vorstand sieht „Vorhaben“ immer, Mitglieder nur, wenn sie in einem mitmachen. */
export function useVorhabenInNavigation(): boolean {
  const { rechte } = useAngemeldet();
  const q = useVorhabenUebersicht();
  if (rechte.has("vorhaben.verwalten") || rechte.has("aufgaben.vorstand_lesen")) return true;
  return meineVorhaben(q.data ?? []).length > 0;
}

/** Seitenleiste (BAUPLAN-SEITENLEISTE.md, Abschnitt 7): die eigenen aktiven Vorhaben unter „Vorhaben“, Regeln in kapselUnterpunkte */
export function useVorhabenUnterpunkte(): readonly Unterpunkt[] {
  const q = useVorhabenUebersicht();
  const farben = useRessortFarben();
  return useMemo(() => kapselUnterpunkte(q.data ?? [], farben.data), [q.data, farben.data]);
}

/** Untere Leiste (Abschnitt 9.1): Vorhaben statt Offen, wenn die Person in einem Vorhaben ist. Sprint geht vor (sprint.planen). */
export function useInEinemVorhaben(): boolean {
  return meineVorhaben(useVorhabenUebersicht().data ?? []).length > 0;
}

export interface VorhabenPerson {
  person: string;
  rolle: VorhabenRolle;
  angelegt_am: string;
}

/** Personen eines Vorhabens mit Rolle, Leitung zuerst (RLS: nur sichtbare Vorhaben) */
export function useVorhabenPersonen(id: string | undefined) {
  return useQuery({
    queryKey: ["vorhaben", "personen", id],
    enabled: !!id,
    refetchInterval: 60_000,
    queryFn: async (): Promise<VorhabenPerson[]> => {
      const { data, error } = await db().from("aufgaben_vorhaben_personen").select("person, rolle, angelegt_am").eq("vorhaben_id", id ?? "").order("angelegt_am");
      if (error) throw error;
      return data
        .map((x) => ({ person: x.person, rolle: rolle(x.rolle) ?? "mitwirkend", angelegt_am: x.angelegt_am }))
        .sort((a, b) => Number(a.rolle !== "leitend") - Number(b.rolle !== "leitend"));
    },
  });
}

export interface NeuesVorhaben {
  name: string;
  beschreibung: string;
  beginn: string | null;
  ende: string | null;
  leitung: string | null;
}

/** Neues Vorhaben mit Art (Modal, Abschnitt 5). Antragsfelder nur beim Förderantrag, beantragt in Cent. */
export interface NeuesVorhabenMitArt extends NeuesVorhaben {
  art: VorhabenArt;
  gehoert_zu?: string | null;
  antrag_stand?: AntragStand;
  foerdergeber?: string;
  antragsfrist?: string | null;
  beantragt?: number | null;
}

/** Daten für aufgaben_vorhaben_neu_mit_art: leere Felder fallen weg, Euro statt Cent */
export function datenMitArt(v: NeuesVorhabenMitArt): Record<string, Json> {
  const d: Record<string, Json> = { name: v.name, beschreibung: v.beschreibung, art: v.art };
  if (v.beginn) d.beginn = v.beginn;
  if (v.ende) d.ende = v.ende;
  if (v.leitung) d.leitung = v.leitung;
  if (v.art === "foerderantrag") {
    if (v.gehoert_zu) d.gehoert_zu = v.gehoert_zu;
    if (v.antrag_stand) d.antrag_stand = v.antrag_stand;
    if (v.foerdergeber) d.foerdergeber = v.foerdergeber;
    if (v.antragsfrist) d.antragsfrist = v.antragsfrist;
    if (v.beantragt !== null && v.beantragt !== undefined) d.beantragt = v.beantragt / 100;
  }
  return d;
}

/** Schreiben über die RPCs aus Paket C1. Danach Vorhaben, Aufgaben und Mitteilungen neu laden. */
export function useVorhabenAktionen() {
  const qc = useQueryClient();
  const neu = () => {
    for (const k of ["vorhaben", "aufgaben", "aufgabe", "mitteilungen"]) void qc.invalidateQueries({ queryKey: [k] });
  };
  const rpc = async <T,>(fn: PromiseLike<{ data: T; error: unknown }>): Promise<T> => {
    const { data, error } = await fn;
    if (error) throw error;
    return data;
  };
  return {
    anlegen: useMutation({
      mutationFn: (v: NeuesVorhaben) =>
        rpc(
          db().rpc("aufgaben_vorhaben_neu", {
            p_name: v.name,
            p_beschreibung: v.beschreibung,
            ...(v.beginn ? { p_beginn: v.beginn } : {}),
            ...(v.ende ? { p_ende: v.ende } : {}),
            ...(v.leitung ? { p_leitung: v.leitung } : {}),
          }),
        ),
      onSettled: neu,
    }),
    // Förderantrag und Projekt (Abschnitt 5). Eine Veranstaltung legt das Modal weiter mit aufgaben_vorhaben_neu an, das geht immer.
    anlegenMitArt: useMutation({
      mutationFn: (v: NeuesVorhabenMitArt) => rpc(db().rpc("aufgaben_vorhaben_neu_mit_art", { p_daten: datenMitArt(v) })),
      onSettled: neu,
    }),
    // Beträge in Cent, die Datenbank speichert Euro
    summenSetzen: useMutation({
      mutationFn: ({ id, beantragt, bewilligt }: { id: string; beantragt: number | null; bewilligt: number | null }) =>
        rpc(
          db().rpc("aufgaben_vorhaben_summen_setzen", {
            p_vorhaben: id,
            ...(beantragt === null ? {} : { p_beantragt: beantragt / 100 }),
            ...(bewilligt === null ? {} : { p_bewilligt: bewilligt / 100 }),
          }),
        ),
      onSettled: neu,
    }),
    bearbeiten: useMutation({
      mutationFn: ({ id, aenderung }: { id: string; aenderung: Record<string, Json> }) => rpc(db().rpc("aufgaben_vorhaben_bearbeiten", { p_id: id, p_aenderung: aenderung })),
      onSettled: neu,
    }),
    archivieren: useMutation({
      mutationFn: ({ id, archiviert }: { id: string; archiviert: boolean }) => rpc(db().rpc("aufgaben_vorhaben_archivieren", { p_id: id, p_archiviert: archiviert })),
      onSettled: neu,
    }),
    personSetzen: useMutation({
      mutationFn: ({ id, person, rolle }: { id: string; person: string; rolle: VorhabenRolle }) =>
        rpc(db().rpc("aufgaben_vorhaben_person_setzen", { p_vorhaben: id, p_person: person, p_rolle: rolle })),
      onSettled: neu,
    }),
    personEntfernen: useMutation({
      mutationFn: ({ id, person }: { id: string; person: string }) => rpc(db().rpc("aufgaben_vorhaben_person_entfernen", { p_vorhaben: id, p_person: person })),
      onSettled: neu,
    }),
  };
}
